import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaPaperPlane } from "react-icons/fa";
import { css, spacing, uiColors } from "../library";
import { colors, styles } from "../library/theme";

// Variações do accent (#e04942) sem token dedicado no theme.
const accentStrong = "#c73b34";
const accentBorder = "rgba(224, 73, 66, 0.55)";
const accentFocus = "rgba(224, 73, 66, 0.14)";
const inkFaint = colors.greyLight;

const composer = css({
  borderTop: `1px solid ${uiColors.border}`,
  padding: `14px ${spacing.m}`,
  backgroundColor: colors.white,
  display: "flex",
  gap: 10,
  alignItems: "flex-end",
});
const field = css({
  flex: 1,
  display: "flex",
  alignItems: "center",
  backgroundColor: colors.white,
  border: `1px solid ${uiColors.border}`,
  borderRadius: 999,
  padding: "4px 6px 4px 18px",
  transition: "border-color .15s ease, box-shadow .15s ease",
  ":focus-within": {
    borderColor: accentBorder,
    boxShadow: `0 0 0 4px ${accentFocus}`,
  },
});
const textarea = css(styles.defaultFontFamily, {
  flex: 1,
  resize: "none",
  border: 0,
  background: "none",
  outline: "none",
  fontSize: 14,
  lineHeight: 1.4,
  color: colors.greyDarker,
  padding: "11px 6px",
  maxHeight: 120,
  "::placeholder": { color: inkFaint },
});
const send = css({
  flex: "0 0 42px",
  width: 42,
  height: 42,
  borderRadius: "50%",
  border: 0,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
  backgroundColor: uiColors.theme,
  color: colors.white,
  transition: "transform .12s ease, background .12s ease",
  ":hover:not(:disabled)": { backgroundColor: accentStrong, transform: "scale(1.06)" },
  ":disabled": { backgroundColor: colors.decorationSoft, cursor: "default", transform: "none" },
});

export default function ChatComposer({ onSend, disabled }) {
  const [text, setText] = useState("");

  const textareaRef = useRef(null);
  const wasDisabled = useRef(false);

  // Ao reabilitar o campo (resposta recebida ou erro), devolve o foco para o
  // aluno seguir digitando sem precisar clicar de novo no campo.
  useEffect(() => {
    if (wasDisabled.current && !disabled) {
      textareaRef.current?.focus();
    }
    wasDisabled.current = disabled;
  }, [disabled]);

  const submit = useCallback(() => {
    const message = text.trim();
    if (!message || disabled) return;

    onSend(message);
    setText("");
  }, [text, disabled, onSend]);

  const changeHandler = useCallback((event) => setText(event.target.value), []);

  const keyDownHandler = useCallback(
    (event) => {
      if (event.key !== "Enter" || event.shiftKey) return;

      event.preventDefault();
      submit();
    },
    [submit]
  );

  return (
    <div className={composer}>
      <div className={field}>
        <textarea
          ref={textareaRef}
          className={textarea}
          rows={1}
          value={text}
          onChange={changeHandler}
          onKeyDown={keyDownHandler}
          placeholder="Escreva sua mensagem…"
          disabled={disabled}
        />
      </div>
      <button
        type="button"
        className={send}
        onClick={submit}
        disabled={disabled || !text.trim()}
        aria-label="Enviar mensagem"
      >
        <FaPaperPlane size={17} />
      </button>
    </div>
  );
}
